import {
  Component,
  Input,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ParticipantMedia,
  LiveKitRoomService,
} from '../../../../core/services/livekit/livekit-room.service';

import { TranslateModule } from '@ngx-translate/core';
import { DoctorNamePipe } from '../../../../core/pipes/doctor-name.pipe';

@Component({
  selector: 'app-participant-tile',
  standalone: true,
  imports: [CommonModule, TranslateModule, DoctorNamePipe],
  templateUrl: './participant-tile.component.html',
  styleUrls: ['./participant-tile.component.scss'],
})
export class ParticipantTileComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  @Input() participant: ParticipantMedia | null | undefined;
  @Input() isLocal = false;

  @ViewChild('videoElement') set videoElement(
    el: ElementRef<HTMLVideoElement> | undefined
  ) {
    if (el) {
      this._videoElement = el;
      this.attachMedia();
    }
  }
  private _videoElement?: ElementRef<HTMLVideoElement>;

  @ViewChild('audioElement') set audioElement(
    el: ElementRef<HTMLAudioElement> | undefined
  ) {
    if (el) {
      this._audioElement = el;
      this.attachMedia();
    }
  }
  private _audioElement?: ElementRef<HTMLAudioElement>;

  private attached = false;

  constructor(private liveKitRoom: LiveKitRoomService) {}

  ngAfterViewInit(): void {
    this.attachMedia();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['participant'] && !changes['participant'].firstChange) {
      this.attachMedia();
    }
  }

  ngOnDestroy(): void {
    this.detachMedia();
  }

  /**
   * Get participant initials for avatar
   */
  getInitials(): string {
    const name = this.participant?.name || '';
    if (!name) return 'U';

    const parts = name.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[1]?.[0] || '' : '';

    return (first + last).toUpperCase() || 'U';
  }

  /**
   * Attach media tracks to video/audio elements
   */
  private attachMedia(): void {
    if (!this.participant) return;

    const vEl = this._videoElement?.nativeElement;
    const aEl = this._audioElement?.nativeElement;

    // Attach video
    if (vEl && this.participant.isCameraEnabled) {
      if (this.isLocal) {
        this.liveKitRoom.attachLocalVideo(vEl);
      } else {
        this.liveKitRoom.attachParticipantVideo(this.participant.identity, vEl);
      }
    }

    // Attach audio for remote participants
    if (aEl && !this.isLocal && this.participant.isMicEnabled) {
      this.liveKitRoom.attachParticipantAudio(this.participant.identity, aEl);
    }

    this.attached = true;
  }

  /**
   * Detach media tracks
   */
  private detachMedia(): void {
    if (this._videoElement?.nativeElement) {
      this._videoElement.nativeElement.srcObject = null;
    }
    if (this._audioElement?.nativeElement) {
      this._audioElement.nativeElement.srcObject = null;
    }
    this.attached = false;
  }
}
